import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import * as paymentsApi from '../../api/payments'
import { useUIStore } from '../../stores/uiStore'
import Card from '../common/Card'
import Button from '../common/Button'
import Badge from '../common/Badge'
import Spinner from '../common/Spinner'
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline'

/**
 * Payment Schedule Manager Component
 * Full payment management for signed contracts
 */
export default function PaymentScheduleManager({ contractId }) {
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  const [filter, setFilter] = useState('all') // all, scheduled, paid
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false)
  const [notes, setNotes] = useState('')

  // Fetch payments
  const { data: payments, isLoading } = useQuery({
    queryKey: ['contract', contractId, 'payments'],
    queryFn: () => paymentsApi.getContractPayments(contractId),
  })

  // Fetch payment summary
  const { data: summary } = useQuery({
    queryKey: ['contract', contractId, 'payments', 'summary'],
    queryFn: () => paymentsApi.getPaymentSummary(contractId),
  })

  // Mark as paid mutation
  const markPaidMutation = useMutation({
    mutationFn: ({ paymentId, paidDate, notes }) =>
      paymentsApi.updatePaymentStatus(paymentId, 'paid', paidDate, notes),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract', contractId, 'payments'])
      showToast('Payment marked as paid', 'success')
      setShowMarkPaidModal(false)
      setSelectedPayment(null)
      setNotes('')
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to update payment', 'error')
    },
  })

  const handleMarkAsPaid = (payment) => {
    setSelectedPayment(payment)
    setShowMarkPaidModal(true)
  }

  const confirmMarkAsPaid = () => {
    if (!selectedPayment) return
    
    const paidDate = new Date().toISOString().split('T')[0]
    markPaidMutation.mutate({
      paymentId: selectedPayment.id,
      paidDate,
      notes: notes.trim() || null
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!payments || payments.length === 0) {
    return (
      <Card>
        <Card.Body>
          <div className="text-center py-8">
            <p className="text-gray-500">No payments found for this contract</p>
          </div>
        </Card.Body>
      </Card>
    )
  }

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    if (filter === 'all') return true
    return payment.status === filter
  })

  return (
    <>
      <div className="space-y-6">
        {/* Summary Card */}
        {summary && (
          <Card>
            <Card.Body>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="text-2xl font-bold text-gray-900">
                    €{summary.total_amount?.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Paid</p>
                  <p className="text-2xl font-bold text-green-600">
                    €{summary.total_paid?.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Remaining</p>
                  <p className="text-2xl font-bold text-orange-600">
                    €{summary.total_remaining?.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Progress</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.payments_paid}/{summary.total_payments}
                  </p>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all"
                      style={{
                        width: `${(summary.payments_paid / summary.total_payments) * 100}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All ({payments.length})
          </Button>
          <Button
            variant={filter === 'scheduled' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilter('scheduled')}
          >
            Scheduled ({payments.filter(p => p.status === 'scheduled').length})
          </Button>
          <Button
            variant={filter === 'paid' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilter('paid')}
          >
            Paid ({payments.filter(p => p.status === 'paid').length})
          </Button>
        </div>

        {/* Payments Table */}
        <Card>
          <Card.Body>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scheduled Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {payment.payment_number}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(payment.scheduled_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        €{payment.amount.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        {payment.status === 'paid' ? (
                          <Badge variant="success">
                            <CheckCircleIcon className="h-4 w-4 mr-1 inline" />
                            Paid
                          </Badge>
                        ) : (
                          <Badge variant="warning">
                            <ClockIcon className="h-4 w-4 mr-1 inline" />
                            Scheduled
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.paid_date ? (
                          new Date(payment.paid_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                        {payment.status === 'scheduled' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleMarkAsPaid(payment)}
                          >
                            Mark as Paid
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Mark as Paid Modal */}
      {showMarkPaidModal && selectedPayment && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-30" onClick={() => setShowMarkPaidModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Mark Payment as Paid
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Payment #{selectedPayment.payment_number} - €{selectedPayment.amount.toFixed(2)}
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Add any notes about this payment..."
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowMarkPaidModal(false)
                    setSelectedPayment(null)
                    setNotes('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={confirmMarkAsPaid}
                  loading={markPaidMutation.isPending}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

PaymentScheduleManager.propTypes = {
  contractId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}